import React, {useEffect, useState} from 'react';
import CompaniesHero from '../../../features/Components/Companies/CompaniesHero';
import Customers from '../../../features/Components/Companies/Customers';
import InputMUISearch from '../../../shared/InputMUISearch';
import './CompaniesCustomers.scss';
import {useDispatch, useSelector} from 'react-redux';
import {
  getCompaniesClientsList,
  patchCompanyClientBlock,
  patchCompanyClientReturn
} from '../../../redux/actions/clients/clientsActions';
import LoadPanel from '../../Components/LoadPanel';
import NoItems from '../../Components/NoItems';
import {notifySuccess} from '../../../helpers/notifySnack';
import {useTranslation} from 'react-i18next';
import {hasPermission} from '../../../helpers/functions';
import {PERMISSIONS} from '../../../helpers/constants';
import {setModal, setModalData, showConfirmPopup} from '../../../redux/actions/app/appActions';
import {mainPath} from '../../../routes/paths';
import {useHistory} from 'react-router-dom';
import {POPUPS} from '../../../redux/actions/app/appActionTypes';
import {getCompany} from "../../../redux/actions/company/companyActions";

const CompaniesCustomers = () => {
  const history = useHistory();
  const {t} = useTranslation();
  const {singleCompany} = useSelector((state) => state.company);
  const [loading, setLoading] = useState(false);
  const [searchCompanies, setSearchCompanies] = useState('');
  const dispatch = useDispatch();
  const {companiesClientsList, companiesClientsListLoading} = useSelector((state) => state.clients);
  const profileMe = useSelector((state) => state.profile.profileMe);

  useEffect(() => {
    if (companiesClientsListLoading && !companiesClientsList.length) {
      dispatch(getCompaniesClientsList(singleCompany.id));
    }
  }, [dispatch, singleCompany.id, companiesClientsListLoading]);

  const handleChange = (event) => {
    setSearchCompanies(event.target.value);
  };

  const handleClear = () => {
    setSearchCompanies('');
  };

  const handleId = async (id) => {
    history.push(`${mainPath.companiesCustomersSingle.replace(':id', singleCompany?.id).replace(':clientId', id)}`);
  };
  const handleBack = () => {
    history.push(mainPath.companiesCustomers.replace(':id', singleCompany?.id));
  };

  const handleAdd = () => {
    history.push(`${mainPath.companiesCustomersAdd.replace(':id', singleCompany?.id)}`);
  };

  const handleBlock = async (id) => {
    setLoading(true);
    try {
      const response = await dispatch(patchCompanyClientBlock(singleCompany.id, id));
      if (response.payload?.status === 200) {
        await Promise.all([dispatch(getCompaniesClientsList(singleCompany.id)), dispatch(getCompany(singleCompany.id))]);
        notifySuccess(t('all.successfully'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = async (id) => {
    setLoading(true);
    try {
      const response = await dispatch(patchCompanyClientReturn(singleCompany.id, id));
      if (response.payload?.status === 200) {
        await Promise.all([dispatch(getCompaniesClientsList(singleCompany.id)), dispatch(getCompany(singleCompany.id))]);
        notifySuccess(t('all.successfully'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBlockRedux = (id) => {
    dispatch(
      showConfirmPopup({
        title: t('participant.blockTitle'),
        text: t('participant.blockText'),
        visible: true,
        confirmCallback: () => handleBlock(id)
      })
    );
  };

  const handleReturnRedux = (id) => {
    dispatch(
      showConfirmPopup({
        visible: true,
        title: t('participant.unblockTitle'),
        text: t('participant.unblockText'),
        confirmCallback: () => handleReturn(id)
      })
    );
  };
  const filteredClients = Array.isArray(companiesClientsList)
    ? companiesClientsList.filter((client) => client.firstName.toLowerCase().includes(searchCompanies.toLowerCase()))
    : [];

  const createPermission = hasPermission(PERMISSIONS.CUSTOMER_CREATE, profileMe?.permissions ?? []);
  const editPermission = hasPermission(PERMISSIONS.CUSTOMER_EDIT, profileMe?.permissions ?? []);
  const editPerson = hasPermission(PERMISSIONS.USER_FULL_EDIT, profileMe?.permissions ?? []);
  const blockCustomerPermission = hasPermission(PERMISSIONS.CUSTOMER_BLOCK, profileMe?.permissions ?? []);
  const returnCustomerPermission = hasPermission(PERMISSIONS.CUSTOMER_RETURN, profileMe?.permissions ?? []);
  const onEditUser = (employeeId) => {
    dispatch(setModal(POPUPS.EDIT_USER));
    dispatch(setModalData(employeeId));
  };

  if (companiesClientsListLoading) {
    return (
      <div className={'companies-page-loader'}>
        <LoadPanel noBg={true} circleSize={50} staticPos={true} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={'companies-page-loader'}>
        <LoadPanel noBg={true} circleSize={50} staticPos={true} />
      </div>
    );
  }

  return (
    <div className='companies-customers-wrapper'>
      <div className='companies-customers'>
        <div className='companies-name'>{singleCompany?.name ?? ''}</div>

        <CompaniesHero
          heading={`${companiesClientsList.length} ${t('CompaniesCustomers.clients_count')}`}
          subHeading={t('CompaniesCustomers.clients')}
          selectedAdd={true}
          selectedList={true}
          handleAdd={handleAdd}
          handleList={handleBack}
          disabledActionAdd={!createPermission}
        />

        <>
          {companiesClientsList.length > 0 ? (
            <div className='companies-customers__content'>
              <InputMUISearch
                className='search-box__input'
                fullWidth
                value={searchCompanies}
                onClear={handleClear}
                onChange={handleChange}
                placeholder={t('CompaniesCustomers.search')}
              />

              <div className='companies-customers__list-wrapper'>
                <div className='companies-customers__list'>
                  {filteredClients.map(
                    ({id, firstName, role, roleName, isActive, avatar, clientId, phone, email, projectNames}) => (
                      <Customers
                        key={id}
                        firstName={firstName}
                        position={role}
                        roleName={roleName}
                        isActive={isActive}
                        id={id}
                        avatar={avatar}
                        handleId={() => handleId(clientId)}
                        onBlock={handleBlockRedux}
                        onReturn={handleReturnRedux}
                        editPermission={editPermission}
                        editPerson={editPerson}
                        onEditUser={onEditUser}
                        phone={phone}
                        email={email}
                        projectNames={projectNames}
                        blockCustomerPermission={blockCustomerPermission}
                        returnCustomerPermission={returnCustomerPermission}
                      />
                    )
                  )}
                </div>
              </div>
            </div>
          ) : (
            <NoItems
              description={t('CompaniesCustomers.no_clients_description')}
              buttonText={t('CompaniesCustomers.client_button')}
              title={t('CompaniesCustomers.no_clients_title')}
              handleNavigate={handleAdd}
              center={'center'}
            />
          )}
        </>
      </div>
    </div>
  );
};

export default CompaniesCustomers;
